<script setup>

import JQrcodeRect from "@/v2/components/qrcode/rect/index.vue";
import JQrcodePasswd from "@/v2/components/qrcode/passwd/index.vue";
import JQrcodeDescription from "@/v2/components/qrcode/description/index.vue";
import {computed, nextTick, onMounted, onUnmounted, reactive, ref, watch} from "vue";
import {QrcodeEnum, QrcodeRequestEnum, QrcodeRespondStatusEnum, QrcodeStatusEnum, QrcodeStreamEventEnum} from "@/v2/enumerate/qrcode";
import {getFactoryQrcodeStatus} from "@/v2/api/module/factory";
import {useRouter} from "vue-router";
import {useAuthorizationSession} from "@/v2/hooks/authorization/session";
import {useAuthorization} from "@/v2/hooks/authorization";
import {useSetting} from "@/v2/hooks/setting";
import {SettingLoginEnum} from "@/v2/enumerate/setting";
import {sprintf} from "sprintf-js";
import {useUtils} from "@/v2/hooks/utils";
import {useSse} from "@/hook/useSse"

const router = useRouter()

const component = ref()

const {
  getUuid
} = useUtils()

/**
 * 顯示端輪詢掃碼狀態的間隔 (毫秒)
 */
const POLLING_INTERVAL = 1500

const {
  setAuthorized: setUserAuthorized
} = useSetting()

const {
  setLogin: setUserLogin
} = useAuthorization()

const {
  getUuid: getUserUuid,
  getChurch: getUserChurch,
  setMapping: setUserMapping
} = useAuthorizationSession()

/**
 * 轉入 - env - 參數
 */
const meta = {
  //網址
  url: {
    //二維碼
    qrcode: (import.meta.env.VITE_BASE_URL).replaceAll(/\/$/gi, "")
  },
  //超時
  expire: {
    //持續時間
    duration: parseInt(import.meta.env.VITE_FACTORY_QRCODE_EXPIRE)
  }
}

/**
 * 參數 - 外部 - 提交
 */
const emit = defineEmits(["submit", "cancel", "authorize", "keyup", "changeMode"])

/**
 * 傳入 - 參數
 */
const props = defineProps({
    mode: { type: null, required: true },
    data: { type: null, required: false },
    delay: { type: Number, required: false, default: 550 },
    isCancelClose: { type: Boolean, required: false, default: false },
    marginTop: { type: Number, required: false, default: 0 },
    marginBottom: { type: Number, required: false, default: 2 },
    placeholder: { type: String, required: false, default: "" }
  })

/**
 * 本地 - 參數
 */
const active = reactive({
  i18n: "component.qrcode.v2",
  status: null,
  passwd: false,
  timer: null,
  polling: null,
  token: null,
  expire: meta.expire.duration,
  blob: null,
  is: {
    qrcode: {
      icon: {
        size: 28
      }
    }
  }
})

/**
 * 欄位
 */
const field = reactive({
  passwd: ""
})

/**
 * 開啟
 */
const onOpen = () => {

  //填入本次執行的唯一識別碼
  active.token = getUuid(32, true)

  //首次獲取驗證二維碼
  setTimeout(() => onFetch(), 300)

}

/**
 * 關閉
 */
const onClose = () => {

  //清除計時器
  if (active.timer) clearInterval(active.timer)

  //停止輪詢
  onStopPolling()

}

const {startSse, stopSse} = useSse()

/**
 * 停止 - 輪詢掃碼狀態（含即時推播連線）
 *
 * ⚠️ 刻意把 stopSse() 一起做掉：呼叫端裡「已授權」那處的註解寫著
 *    「權杖為一次性領取, 必須先停止輪詢」—— 接上 SSE 之後那條不變式必須連推播一起停，
 *    否則下一則事件會再去領一次已經 evict 的權杖。綁在同一個函式裡，呼叫端就都不用改。
 */
const onStopPolling = () => {

  stopSse()

  if (active.polling) clearInterval(active.polling)

  active.polling = null

}

/**
 * 開始 - 監看掃碼狀態
 *
 * 優先走 SSE 即時推播；連不上（舊瀏覽器 / 代理把長連線切掉）才退回輪詢。
 * ⚠️ 推播事件刻意不帶登入權杖，只當「狀態變了」的訊號 —— 收到後仍呼叫 onPolling()
 *    去 /api/factory/qrcode/status 領取，那支端點「終態領取後即 evict」的一次性語意完全不變。
 */
const onStartPolling = () => {

  //避免重複啟動
  onStopPolling()

  //沒有 token 就沒有東西可監看
  if (!active.token) return

  const connected = startSse(
      `/api/factory/qrcode/stream?token=${active.token}`,
      [QrcodeStreamEventEnum.INSPECTION, QrcodeStreamEventEnum.AUTHORIZE, QrcodeStreamEventEnum.CANCEL],
      () => onPolling(),
      () => onStartPollingByInterval()
  )

  if (!connected) onStartPollingByInterval()

}

/**
 * 降級 - 定時輪詢（SSE 不可用時的後路，程式碼刻意保留）
 */
const onStartPollingByInterval = () => {

  if (active.polling) clearInterval(active.polling)

  active.polling = setInterval(() => onPolling(), POLLING_INTERVAL)

}

/**
 * 輪詢 - 掃碼狀態
 */
const onPolling = async () => {

  if (!active.token) return

  const {
    data
  } = await getFactoryQrcodeStatus(active.token)

  //取不到內容 (連線異常), 下一輪再試
  if (!data?.status) return

  //已掃描待授權, UI顯示掃碼成功
  if (data.status === QrcodeRespondStatusEnum.INSPECTED) onInspection()
  //用戶端在App上按確認授權 (權杖為一次性領取, 必須先停止輪詢)
  else if (data.status === QrcodeRespondStatusEnum.AUTHORIZED) {
    onStopPolling()
    clearInterval(active.timer)
    onAuthorize(data.token ?? null)
  }
  //用戶端在App上取消
  else if (data.status === QrcodeRespondStatusEnum.CANCELLED) {
    onStopPolling()
    onCancel()
  }

}

/**
 * 切換 - 模式
 */
const onChangeMode = () => emit("changeMode", !active.passwd)

/**
 * 掃碼檢驗成功
 * UI顯示掃碼成功
 */
const onInspection = () => active.status = QrcodeStatusEnum.INSPECTION

/**
 * 用戶端在App上按確認授權
 * 完成準備跳轉
 * @param token 輪詢取回的登入權杖
 */
const onAuthorize = (token) => {

  //顯示登入成功
  active.status = QrcodeStatusEnum.AUTHORIZED

  setTimeout(()=>{

    // 登入
    if (props.mode === QrcodeEnum.LOGIN) {

      //權杖已由 /api/factory/qrcode/status 寫進 cookie
      if (token) setUserLogin(true)

    }
    // 安全性綁定
    else if (props.mode === QrcodeEnum.MAPPING) {

        //強迫登入改為Qrcode預設
        setUserAuthorized(SettingLoginEnum.QRCODE)

        //修改為用戶與管理員對應成功
        setUserMapping(true)

    }

    // 二級驗證 呼叫 emit
    if (props.mode === QrcodeEnum.FACTORY) emit(`authorize`)
    // 登入或安全性綁定
    else router.push({
      name: `AdminDashboard`,
    })

  }, props.delay)

}

/**
 * 取消
 */
const onCancel = () => {

  // 二級驗證 呼叫 emit
  if (props.mode === QrcodeEnum.FACTORY) emit(`cancel`)
  //其他狀況, 呼叫超時即可
  else onExpire()

}

/**
 * 超時
 */
const onExpire = () => {

  //清除計時器
  clearInterval(active.timer)

  //停止輪詢 (二維碼已失效, 再輪也只會拿到 pending)
  onStopPolling()

  //狀態 - 已經超時
  active.status = QrcodeStatusEnum.EXPIRE

}

/**
 * 取得二維碼圖
 */
const onFetch = () => {

  //清除blob (讓讀取轉動重轉)
  active.blob = null

  //清除狀態
  active.status = null

  //清除計時器
  clearInterval(active.timer)

  //停止上一輪的輪詢 (二維碼重取後 token/authorize 已被覆蓋)
  onStopPolling()

  //啟動xhr
  const xhr = new XMLHttpRequest()

  //當xhr載入時
  xhr.onload = () => {

    //初始化檔案讀取
    const reader = new FileReader()

    //檔案讀取嘗試讀取xhr回應內容
    reader.readAsDataURL(xhr.response)

    //當檔案讀取完成時
    reader.onloadend = () => {

      //填入二維碼的blob
      active.blob = reader.result;

      //監控超時
      active.timer = setInterval(()=>onExpire(), active.expire)

      //二維碼顯示後才開始輪詢掃碼狀態
      onStartPolling()

    }

  }

  //指定開啟方式與路徑
  xhr.open('POST', sprintf(
      "%s/qrcode/factory",
      meta.url.qrcode
  ))

  //指定回應類型
  xhr.responseType = 'blob'

  //指定請求header格式
  xhr.setRequestHeader("Content-Type", "application/json; charset=UTF-8")

  //權杖在 httpOnly cookie；後端只在帶了這個 header 時才認 cookie（綁定時要用）
  xhr.setRequestHeader("X-Admin-Client", "1")

  //指定請求參數
  xhr.send(JSON.stringify({
    //請求方式
    "method": props.mode === QrcodeEnum.MAPPING ? QrcodeRequestEnum.MAPPING : QrcodeRequestEnum.FACTORY,
    //本次連線權證
    "token": active.token,
    //用戶uuid
    "uuid": props.data?.uuid ?? getUserUuid.value,
    //組織uuid
    "organization": getOrganization.value.uuid
  }))

}

/**
 * 取得 - 外觀
 */
const getVariant = computed(() => [
  `mt-${props.marginTop}`,
  `mb-${props.marginBottom}`
])

/**
 * 取得 - 機構 - 內容
 */
const getOrganization = computed(() =>{
  return {
    seq: props.data?.organization.seq ?? 1,
    uuid: props.data?.organization.uuid ?? getUserChurch.value?.organization.uuid,
    name: props.data?.organization.name ?? getUserChurch.value?.organization.name,
  }
})

//切換到密碼輸入的時候, 自動focus密碼框
watch(()=>active.passwd, async (e) => {

  //非密碼模式, 不往下執行
  if (!e) {
    return
  }

  //等待渲染完成
  await nextTick()

  //focus到密碼框
  component.value?.querySelector(`.el-input__inner`)?.focus()

})

/**
 * 生命週期 - 掛起
 */
onMounted(() => onOpen())

/**
 * 生命週期 - 銷毀
 */
onUnmounted(() => onClose())

</script>

<template>
  <div :class="getVariant"
       ref="component"
       class="qrcode d-flex-column d-flex justify-content-center align-items-center">
    <!-- 二維碼顯示區 (含狀態圖) -->
    <j-qrcode-rect v-if="!active.passwd"
                   @refresh="onFetch"
                   :mode="props.mode"
                   :active="active" />
    <!-- 密碼輸入框 -->
    <j-qrcode-passwd v-else
                     @update="emit('keyup', field.passwd)"
                     @submit="emit('submit', field.passwd)"
                     :field="field"
                     :placeholder="placeholder" />
    <!-- 描述區 - 下載App/切換模式 -->
    <j-qrcode-description @change="onChangeMode"
                          :mode="props.mode"
                          :organization="getOrganization"
                          :active="active" />
  </div>
</template>

<style scoped lang="scss">

</style>